'use client';

import React, { useState } from 'react';
import { FlipCardComponent } from '../types/content-components.types.js';
import { ComponentIcon } from './ComponentIcon.js';
import { useComponentAnimation } from './hooks/useComponentAnimation.js';
import { RotateCw } from 'lucide-react';

interface FlipCardProps {
  component: FlipCardComponent;
}

export const FlipCard: React.FC<FlipCardProps> = ({ component }) => {
  const { props } = component;
  const { ref, isVisible } = useComponentAnimation(props.animation);
  const [isFlipped, setIsFlipped] = useState(false);

  const getVariantColor = () => {
    switch (props.variant) {
      case 'primary':
        return { bg: 'bg-learn-sage-50', border: 'border-learn-sage-300', text: 'text-learn-sage-900', button: 'bg-learn-sage-200 text-learn-sage-700 hover:bg-learn-sage-300' };
      case 'success':
        return { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-900', button: 'bg-green-200 text-green-700 hover:bg-green-300' };
      case 'warning':
        return { bg: 'bg-learn-honey-50', border: 'border-learn-honey-300', text: 'text-learn-honey-900', button: 'bg-learn-honey-200 text-learn-honey-700 hover:bg-learn-honey-300' };
      case 'info':
        return { bg: 'bg-learn-sky-50', border: 'border-learn-sky-300', text: 'text-learn-sky-900', button: 'bg-learn-sky-200 text-learn-sky-700 hover:bg-learn-sky-300' };
      case 'highlight':
        return { bg: 'bg-learn-coral-50', border: 'border-learn-coral-300', text: 'text-learn-coral-900', button: 'bg-learn-coral-200 text-learn-coral-700 hover:bg-learn-coral-300' };
      default:
        return { bg: 'bg-white', border: 'border-gray-200', text: 'text-gray-900', button: 'bg-gray-100 text-gray-700 hover:bg-gray-200' };
    }
  };

  const colors = getVariantColor();
  const animationClass = isVisible ? 'animate-fadeIn' : '';

  return (
    <div ref={ref} className={`w-full ${animationClass}`} style={{ opacity: isVisible ? undefined : 0 }}>
      <div
        className={`relative ${colors.bg} border-2 ${colors.border} rounded-xl p-6 shadow-md min-h-[200px] transition-all duration-500 cursor-pointer`}
        onClick={() => setIsFlipped(!isFlipped)}
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Front or Back Content */}
        <div className={`transition-opacity duration-300 ${isFlipped ? 'opacity-0 absolute inset-0 p-6' : 'opacity-100'}`}>
          <div className="flex items-start gap-4 mb-3">
            {props.frontIcon && (
              <div className="flex-shrink-0 mt-1">
                <ComponentIcon icon={props.frontIcon} className={`w-6 h-6 ${colors.text}`} />
              </div>
            )}
            <h4 className={`text-lg font-bold ${colors.text} flex-1`}>
              {props.frontTitle}
            </h4>
          </div>
          <p className="text-base text-gray-700 leading-relaxed mb-4">
            {props.frontContent}
          </p>
        </div>

        <div className={`transition-opacity duration-300 ${!isFlipped ? 'opacity-0 absolute inset-0 p-6' : 'opacity-100'}`}>
          <div className="flex items-start gap-4 mb-3">
            {props.backIcon && (
              <div className="flex-shrink-0 mt-1">
                <ComponentIcon icon={props.backIcon} className={`w-6 h-6 ${colors.text}`} />
              </div>
            )}
            <h4 className={`text-lg font-bold ${colors.text} flex-1`}>
              {props.backTitle}
            </h4>
          </div>
          <p className="text-base text-gray-700 leading-relaxed mb-4">
            {props.backContent}
          </p>
        </div>

        {/* Flip Button */}
        <button
          className={`absolute bottom-4 right-4 px-3 py-2 ${colors.button} rounded-lg text-sm font-medium flex items-center gap-2 shadow-sm transition-colors`}
          onClick={(e) => {
            e.stopPropagation();
            setIsFlipped(!isFlipped);
          }}
        >
          <RotateCw className="w-4 h-4" />
          {props.flipLabel || (isFlipped ? 'Flip Back' : 'Flip Card')}
        </button>
      </div>
    </div>
  );
};

